import { faBirthdayCake } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GridColDef } from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import DialogActions from "src/containers/DialogActions/DialogActions";
import DaysPendingQuickfilter from "src/containers/Quickfilters/DaysPendingQuickfilter/DaysPendingQuickfilter";
import { useDaysPendingQuickFilter } from "src/containers/Quickfilters/DaysPendingQuickfilter/useDaysPendingQuickfilter";

import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";

import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import useAuthorization from "../../../../custom-hooks/useAuthorization/useAuthorization";
import useForceUpdate from "../../../../custom-hooks/useForceUpdate/useForceUpdate";
import { PageByEmployee } from "../../../../models/api/page/page-by-employee";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import {
  TnATimesheetConfirmationsDTO,
  TnATimesheetConfirmationsFilter,
} from "../../../../models/tna-timesheet-confirmation";
import ConcatenatedCell from "../../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { ColumnsHeaderTitle } from "../../../../reusable-components/Table/styles";
import Table from "../../../../reusable-components/Table/Table";
import { TnATimesheetConfirmationsApiService } from "../../../../services/tna-timesheet-confirmations/tna-timesheet-confirmations-api.service";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../../utils/constants/filter-tables-constants";
import { intlFormatDateShort, intlFormatDateWithHour } from "../../../../utils/time-utils";
import EmailInfoPopover from "../../../EmailInfoPopover/EmailInfoPopover";
import { fromEmailParamsName } from "../../../NavigateToLoginPage/NavigateToLoginPage";
import { CurrentEmployeesQuickfilter } from "../../../Quickfilters/CurrentEmployeesQuickfilter/CurrentEmployeesQuickfilter";
import { useCurrentEmployeesQuickFilter } from "../../../Quickfilters/CurrentEmployeesQuickfilter/useCurrentEmployeesQuickFilter";
import { EmployeeGroupQuickfilter } from "../../../Quickfilters/EmployeeGroupQuickfilter/EmployeeGroupQuickfilter";
import { useEmployeeGroupQuickFilter } from "../../../Quickfilters/EmployeeGroupQuickfilter/useEmployeeGroupQuickfilter";
import { LegalEntityQuickfilter } from "../../../Quickfilters/LegalEntityQuickfilter/LegalEntityQuickfilter";
import { useLegalEntityQuickFilter } from "../../../Quickfilters/LegalEntityQuickfilter/useLegalEntityQuickFilter";
import QuickfiltersContainer from "../../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { EmployeeNameContainer } from "../styles";
import { TableContainer } from "../styles";
import SignoffConfirmationRequests from "./SignoffConfirmationRequests/SignoffConfirmationRequests";
import { EmployeesToUsersFilter } from "../../../../models/employees-to-users";
import { EmployeesToUsersApiService } from "../../../../services/employees/employees-to-users-api.service";

export interface TnATimesheetConfirmationsRow extends TnATimesheetConfirmationsDTO {
  hasNoAccess: boolean;
}
export interface State {
  pagingByEe: TnATimesheetConfirmationsFilter;
  loading: boolean;
  error: string;
  isSignOffConfirmationOpen: boolean;
}

export interface Props {
  handleNotifyParent?: () => void;
  tableHeight?: number;
  handleParentClose?: () => void;
}

export enum DialogOptions {
  remind = "remind",
  cancel = "cancel",
}

const SendMassSignoffRequests = ({ handleNotifyParent, tableHeight, handleParentClose }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_time_sheet_confirmation";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";
  const [searchParams] = useSearchParams();
  const fromEmail = searchParams.get(fromEmailParamsName);
  const highlightedValue = searchParams.get("hl");
  const selectedRowsDataRef = useRef<TnATimesheetConfirmationsRow[]>([]);
  const selectedEmployeeIdsWithoutAccessRef = useRef<Set<string>>(new Set());
  const [isSignOffConfirmationOpen, setIsSignOffConfirmationOpen] = useState<boolean>(false);
  const apiRef = useGridApiRef();

  const theme = useTheme();
  const { currentUserAuthorizations, currentUserNotificationPreferences } = useAppSelector(
    (state) => state.currentUserReducer
  );

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const {
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    allLegalEntities,
    selectedLegalEntity,
    legalEntityQFIsLoading,
  } = useLegalEntityQuickFilter(true);
  const { employeeGroupQuickFilter, setEmployeeGroupQuickFilter } = useEmployeeGroupQuickFilter();
  const { onlyCurrentEmployeesQuickFilter, setOnlyCurrentEmployeesQuickFilter } = useCurrentEmployeesQuickFilter();
  // const { year, month, handleChangeYearAndMonth } = useChangeYearAndMonth();
  const { daysPendingQuickFilter, setDaysPendingQuickFilter } = useDaysPendingQuickFilter();

  const [state, setState] = useState<State>({
    pagingByEe: {
      pagingByEmployee: [] as PageByEmployee[],
    } as TnATimesheetConfirmationsFilter,
    loading: false,
    error: "",
    isSignOffConfirmationOpen: false,
  });

  const { forceUpdate } = useForceUpdate();

  // #region get selected employee access to system
  const employeeHasAccessToSystem = useCallback(
    async (tnaTimesheetConfirmation: TnATimesheetConfirmationsRow): Promise<boolean> => {
      const employeeId = tnaTimesheetConfirmation.employee?.id;

      if (employeeId) {
        // setState((previousState) => ({ ...previousState, loading: true }));
        try {
          const response = await new EmployeesToUsersApiService().loadItems({
            employeeIds: [employeeId],
          } as EmployeesToUsersFilter);
          if (!response.error) {
            return !!response.items[0].user;
          } else {
            // setState((previousState) => ({ ...previousState, error: response.error }));
            return false;
          }
        } catch (error) {
          // setState((previousState) => ({ ...previousState, error: error as string }));
          return false;
        } finally {
          // setState((previousState) => ({ ...previousState, loading: false }));
        }
      } else {
        return false;
      }
    },
    []
  );
  // #endregion

  // #region handle row selection
  const handleRowSelection = useCallback(async (selectedItems: TnATimesheetConfirmationsRow[]) => {
    console.log(selectedItems);
    const previouslySelectedEmployeesIds = selectedRowsDataRef.current.map((selectedRow) => selectedRow.id);
    const newlySelectedIds = selectedItems.map((selectedRow) => selectedRow.id);

    const newlySelectedRow = selectedItems.find(
      (selectedItem) => !previouslySelectedEmployeesIds.includes(selectedItem.id)
    );

    const deletedRow = previouslySelectedEmployeesIds.find(
      (_prevSelectedId) => !newlySelectedIds.includes(_prevSelectedId)
    );

    if (selectedItems.length === 0) {
      selectedRowsDataRef.current = [];
    } else if (newlySelectedRow) {
      const hasAccessToSystem = await employeeHasAccessToSystem(newlySelectedRow);

      if (hasAccessToSystem) {
        selectedEmployeeIdsWithoutAccessRef.current.delete(newlySelectedRow.id ?? "");
        selectedRowsDataRef.current = [
          ...selectedRowsDataRef.current.filter((_selectedRow) => _selectedRow.id !== newlySelectedRow.id),
          newlySelectedRow,
        ];
        apiRef.current.updateRows([{ ...newlySelectedRow, hasNoAccess: false }]);
      } else {
        selectedEmployeeIdsWithoutAccessRef.current.add(newlySelectedRow.id ?? "");
        selectedRowsDataRef.current = [
          ...selectedRowsDataRef.current.filter((_selectedRow) => _selectedRow.id !== newlySelectedRow.id),
          { ...newlySelectedRow, hasNoAccess: true },
        ];
        apiRef.current.updateRows([{ ...newlySelectedRow, hasNoAccess: true }]);
      }
    } else if (deletedRow) {
      selectedRowsDataRef.current = [
        ...selectedRowsDataRef.current.filter((_selectedRow) => _selectedRow.id !== deletedRow),
      ];
    }
  }, []);
  // #endregion

  // #region handle save
  const handleSave = async () => {
    if (selectedRowsDataRef.current.length === 0) {
      const data = {
        id: "",
        correlationId: "",
        invokerId: "",
        isSucceeded: false,
        isCanceled: false,
        error: t("components.toast.no_employees_selected"),
      };
      // toastifyResponse(data, "failed");
      return;
    }
    // setState((prev) => ({ ...prev, isSignOffConfirmationOpen: true }));
    setIsSignOffConfirmationOpen(true);
  };
  // #endregion

  const handleCloseDialog = () => {
    // setState((prev) => ({ ...prev, isSignOffConfirmationOpen: false }));
    setIsSignOffConfirmationOpen(false);
  };

  // #region handle after save
  const handleAfterSave = () => {
    selectedRowsDataRef.current = [];
    setState((prev) => ({ ...prev, isSignOffConfirmationOpen: false }));
    handleNotifyParent?.();
    forceUpdate();
  };
  // #endregion

  // #region columns
  const columns: GridColDef<TnATimesheetConfirmationsRow>[] = [
    {
      field: "employee.employeeCode, employee.internalId",
      // headerName: `${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`,
      renderHeader: () => (
        <Typography>{`${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => {
        // console.log(row);
        return (
          <>
            <>
              <ConcatenatedCell
                aligned={"left"}
                FirstElement={
                  <EmployeeNameContainer>
                    <Grid sx={{ display: "flex", flexDirection: "column" }}>
                      <span title={row?.employee?.name}>
                        {row?.employee?.name} &nbsp;
                        {row.hasNoAccess && (
                          <span style={{ color: theme.palette.warning.main, fontSize: "12px" }}>
                            ({t(`${tRP}.not_linked_to_user`)})
                          </span>
                        )}
                      </span>
                      {row?.employee?.birthDate && (
                        <Typography variant={"body2"}>
                          <FontAwesomeIcon
                            icon={faBirthdayCake}
                            size="xs"
                          />{" "}
                          &nbsp;
                          {intlFormatDateShort(row.employee.birthDate)}
                        </Typography>
                      )}
                    </Grid>
                  </EmployeeNameContainer>
                }
                SecondElement={
                  <Grid>
                    <Typography
                      style={{ color: theme.palette.text.disabled }}
                      variant={"body2"}
                      title={row?.employee?.legalEntity?.name}
                    >
                      {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
                    </Typography>
                  </Grid>
                }
              />
            </>
          </>
        );
      },
      renderHeader: () => (
        <Grid>
          <Typography>{`${t("common_words.employee")} | ${t("common_words.legal_entity")}`}</Typography>
          <EmailInfoPopover
            autoOpen={!!fromEmail}
            message={"Only values of employees for whom you are an approver are highlighted"}
          />
        </Grid>
      ),
      headerAlign: "center",
      flex: 2,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      renderHeader: () => (
        <Tooltip title={`${t("common_words.tna_employee_group")}`}>
          <ColumnsHeaderTitle>{`${t("common_words.tna_employee_group")}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "asOfDate",
      renderCell: ({ row }) => (
        <span
          title={
            row.asOfDate
              ? `Sign-off ${
                  row.fromDate ? `${intlFormatDateShort(CustomDate.ToDate(row.fromDate) ?? new Date())}-` : ""
                }
              ${intlFormatDateShort(CustomDate.ToDate(row.asOfDate) ?? new Date())}\r\nrequested by
              ${row.createdByUser?.name ?? row.createdByUser?.email} at ${intlFormatDateWithHour(row.createdAt)} `
              : ""
          }
          style={{
            color: !row.isSignedOff ? theme.palette.error.main : "",
          }}
        >
          {row.asOfDate ? intlFormatDateShort(CustomDate.ToDate(row.asOfDate) ?? new Date()) : ""}
        </span>
      ),
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.asOf_date`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.asOf_date`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 2,
      minWidth: 100,
      maxWidth: 130,
      align: "center",
    },
    {
      field: "signedOffAt",
      renderCell: ({ row }) => (
        <span
          title={`${t(`${translationFields}.signedOffAt`)} ${
            row.signedOffAt ? intlFormatDateWithHour(row.signedOffAt) : ""
          } by ${row.signedOffBy?.name ?? row.signedOffBy?.email}`}
        >
          {row.signedAsOfDate ? intlFormatDateShort(CustomDate.ToDate(row.signedAsOfDate) ?? new Date()) : ""}
        </span>
      ),
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.signedAsOf_date`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.signedAsOf_date`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      maxWidth: 130,
      align: "center",
    },
    {
      field: "lastSignoffRequest",
      renderCell: ({ row }) => {
        const getDaysAgo = (date: CustomDate | null): number | null => {
          if (!date) return null;
          const dateObj = CustomDate.ToDate(date);
          if (!dateObj) return null;
          const now = new Date();
          const diffTime = Math.abs(now.getTime() - dateObj.getTime());
          return Math.floor(diffTime / (1000 * 60 * 60 * 24));
        };

        const lastSignffRequestDaysAgo = getDaysAgo(row.lastSignoffRequest);
        const lastReminderDaysAgo = getDaysAgo(row.lastReminderSentOn);

        return (
          <Grid sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
            {row.lastSignoffRequest && (
              <span
                title={`${t(`${translationFields}.lastSignoffRequest`)}: ${intlFormatDateWithHour(
                  CustomDate.ToDate(row.lastSignoffRequest) ?? new Date()
                )}`}
              >
                <Typography>
                  {row.lastSignoffRequest !== null && lastSignffRequestDaysAgo !== null
                    ? lastSignffRequestDaysAgo === 0
                      ? `${t(`common_words.today`)} `
                      : `${
                          lastSignffRequestDaysAgo === 1
                            ? `${t(`common_words.yesterday`)}`
                            : `${t(`common_words.days.days_plural`, {
                                count: lastSignffRequestDaysAgo,
                              })}`
                        } ago`
                    : ""}
                </Typography>
              </span>
            )}
            {row.lastReminderSentOn && (
              <span
                title={`${t(`${translationFields}.lastSignoffReminder`)}: ${intlFormatDateWithHour(
                  CustomDate.ToDate(row.lastReminderSentOn) ?? new Date()
                )}`}
              >
                <Typography variant="body2">
                  {`${t(`${translationFields}.lastSignoffReminder`)}: `}
                  {row.lastReminderSentOn !== null && lastReminderDaysAgo !== null
                    ? lastReminderDaysAgo === 0
                      ? `${t(`common_words.today`)} `
                      : `${
                          lastReminderDaysAgo === 1
                            ? `${t(`common_words.yesterday`)} `
                            : `${t(`common_words.days.days_plural`, {
                                count: lastReminderDaysAgo,
                              })}`
                        } ago`
                    : ""}
                </Typography>
              </span>
            )}
          </Grid>
        );
      },
      renderHeader: () => (
        <Tooltip title={`${t(`${translationFields}.lastSignoffRequest`)}`}>
          <ColumnsHeaderTitle>{`${t(`${translationFields}.lastSignoffRequest`)}`}</ColumnsHeaderTitle>
        </Tooltip>
      ),
      headerAlign: "center",
      align: "center",
      flex: 1.5,
    },
  ];
  // #endregion

  // #region quicqfilters
  const quickfilters = useMemo(() => {
    return (
      <QuickfiltersContainer>
        <LegalEntityQuickfilter
          quickFilters={legalEntitiesQuickFilter}
          handleChangeQuickFilters={setLegalEntitiesQuickFilter}
          allLegalEntitiesOption
          allLegalEntities={allLegalEntities}
        />
        <EmployeeGroupQuickfilter
          quickFilters={employeeGroupQuickFilter}
          handleChangeQuickFilters={setEmployeeGroupQuickFilter}
          allEmployeeGroupsOption
        />
        <CurrentEmployeesQuickfilter
          quickFilters={onlyCurrentEmployeesQuickFilter}
          handleChangeQuickFilters={setOnlyCurrentEmployeesQuickFilter}
        />
        <DaysPendingQuickfilter
          quickFilters={daysPendingQuickFilter}
          handleChangeQuickFilters={setDaysPendingQuickFilter}
        />
        {/* <FilterByYearAndMonth
          filterColor={"filter"}
          year={year}
          month={month}
          handleChangeYearAndMonth={handleChangeYearAndMonth}
          withMaxDate={false}
        /> */}
      </QuickfiltersContainer>
    );
  }, [
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    allLegalEntities,
    employeeGroupQuickFilter,
    setEmployeeGroupQuickFilter,
    onlyCurrentEmployeesQuickFilter,
    setOnlyCurrentEmployeesQuickFilter,
    daysPendingQuickFilter,
    setDaysPendingQuickFilter,
  ]);
  // #endregion

  const serviceApi = useMemo(() => new TnATimesheetConfirmationsApiService(), []);

  const propsFilter = useMemo(
    () =>
      ({
        ...state.pagingByEe,
        ...legalEntitiesQuickFilter.legalEntities,
        ...employeeGroupQuickFilter.employeeGroups,
        daysPending: daysPendingQuickFilter.daysPending.days,
        onlyCurrentEmployees: onlyCurrentEmployeesQuickFilter?.onlyWithCurrentEmployees?.onlyCurrentEmployees,
        // year: 0, // no need for year filter here
        // month: month + 1,
      } as TnATimesheetConfirmationsFilter),
    [
      state.pagingByEe,
      legalEntitiesQuickFilter.legalEntities,
      employeeGroupQuickFilter.employeeGroups,
      daysPendingQuickFilter.daysPending.days,
      onlyCurrentEmployeesQuickFilter?.onlyWithCurrentEmployees?.onlyCurrentEmployees,
    ]
  );

  return (
    <TableContainer>
      {(hasAccess || allowedToViewOnly) && (
        <LoadingOverlay
          loading={state.loading || legalEntityQFIsLoading}
          error={state.error}
        >
          <Table
            apiRef={apiRef}
            translationRootPath={tRP}
            displayTitle={false}
            columns={columns}
            serviceApi={serviceApi}
            propsFilter={propsFilter}
            filter={FT_MTPAL}
            allowEdit={false}
            quickFilters={quickfilters}
            readOnly
            getAllItems={(items) => {
              items.map((_item) => {
                apiRef.current.updateRows([
                  {
                    ..._item,
                    hasNoAccess: false,
                  },
                ]);
              });
            }}
            enableSelection
            handleCheckedRows={handleRowSelection}
            tableHeight={tableHeight}
            depthSelection="employee"
          />
          <DialogActions
            handleCancel={() => {
              selectedRowsDataRef.current = [];
              setState((prev) => ({ ...prev, error: "" }));
              handleParentClose?.();
            }}
            handleSave={handleSave}
            disabledSaveButton={state.loading}
            disabledCancelButton={state.loading}
            loading={state.loading}
            customSaveName={t(`${tRP}.send_requests`)}
          />
        </LoadingOverlay>
      )}
      <SignoffConfirmationRequests
        isOpen={isSignOffConfirmationOpen}
        handleNotifyParent={handleAfterSave}
        defaultLegalEntity={selectedLegalEntity}
        selectedItems={selectedRowsDataRef.current}
        onClose={handleCloseDialog}
      />
    </TableContainer>
  );
};

export default memo(SendMassSignoffRequests);
